import React, { useMemo } from 'react';
import { useGetPaginatedAutomationRuns } from 'pages/Board/hooks/useGetPaginatedAutomationRunData';
import { AutomationRunStatus, BoardFilterView, UpdateUserBoardFilterSettingsMutationFn } from 'shared/types/graphql';
import { useAppSelector } from 'state/hooks';
import BoardCard from '../Cards/BoardCard';
import BoardColumn from './BoardColumn';
import { Appointment } from 'pages/Appointments';
import uniqBy from 'lodash-es/uniqBy';
import useClinicUser from 'shared/hooks/useClinicUser';

interface AutomationColumnProps {
  columnName: string;
  columnColor: string;
  automationRunStatus: AutomationRunStatus;
  onEditAppointment: (appointment: Partial<Appointment>) => void;
  unreadChannelIds?: string[];
  updateBoardSettings: UpdateUserBoardFilterSettingsMutationFn;
}

const AutomationColumn = ({
  columnName,
  columnColor,
  automationRunStatus,
  unreadChannelIds,
  onEditAppointment,
  updateBoardSettings,
}: AutomationColumnProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { boardSettings } = useAppSelector((state) => state.board);

  const {
    automationRuns,
    automationRunsPage,
    setAutomationRunsPage,
    totalAutomationRuns,
    isLoadingAutomationRuns,
    hasNextPage,
  } = useGetPaginatedAutomationRuns({
    clinicId: currentClinicId || '',
    automationRunStatus,
    dateRange: boardSettings?.specificDateRange,
  });

  // Ensure we are only showing unique channels for this channel status id
  const filteredAutomationRuns = useMemo(() => {
    const uniqueAutomationRuns = uniqBy(automationRuns, 'id');
    return uniqueAutomationRuns?.filter((automationRun) => automationRun.status === automationRunStatus);
  }, [automationRunStatus, automationRuns]);

  return (
    <BoardColumn
      columnName={columnName}
      columnColor={columnColor}
      numberOfCards={totalAutomationRuns || 0}
      isLoading={isLoadingAutomationRuns}
      currentPage={automationRunsPage}
      setPageNumber={setAutomationRunsPage}
      hasNextPage={hasNextPage}
      isEmpty={totalAutomationRuns === 0}
      canCollapse={false}
      isCollapsed={false}
      updateBoardSettings={updateBoardSettings}
    >
      {filteredAutomationRuns?.map((automationRun) => {
        const { appointment, id } = automationRun;
        const channelId = appointment?.channel?.id;
        const channelStatus = appointment?.channel?.channelStatus;
        const petParentData = appointment?.clinicPetParents;
        const petData = appointment?.clinicPet;

        return (
          <BoardCard
            key={id}
            hasUnreadMessages={channelId && unreadChannelIds ? unreadChannelIds.includes(channelId) : false}
            onEditAppointment={onEditAppointment}
            appointmentData={{
              id: appointment?.id,
              startAt: appointment?.startAt,
              durationInMinutes: appointment?.durationInMinutes,
            }}
            petParents={petParentData}
            pets={!!petData ? [petData] : undefined}
            channelStatus={channelStatus}
            channelId={channelId}
            cardType={BoardFilterView.Automations}
            automationRunData={automationRun}
            lastMessageCreatedAtDate={appointment?.channel?.lastMessage?.createdAt}
            channelUpdatedAtDate={appointment?.channel?.updatedAt}
          />
        );
      })}
    </BoardColumn>
  );
};

export default AutomationColumn;
